/**
 * Contracts management page with table layout
 * Displays all contracts in a consistent table format with server-side pagination
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react";
import { Plus } from "lucide-react";
import { useSearchParams, useNavigate } from 'react-router';
import {
  fetchAllContracts,
  useContractsPage,
  type ContractsPaginatedOptions,
} from "@/hooks/useContracts";
import { useModuleGate } from "@/hooks/useAccess";
import { CONTRACT_TERMINATION_MODULES } from "@/lib/modules";
import { useListUrlState } from "@/hooks/useListUrlState";
import { useCanOpen } from "@/hooks/useCanOpen";
import { useQueryClient } from "@tanstack/react-query";
import { AllocationsService } from "@/api";
import { allocationKeys } from "@/hooks/useAllocations";
import { findFirstAllocation } from "@/pages/settling-in/findFirstAllocation";
import { toOrgDateOnly } from "@/utils/dateUtils";
import { Button } from "@/components/ui/button";
import { SegmentedToggle } from "@/components/ui/segmented-toggle";
import { QueryErrorState } from "@/components/ui/query-error-state";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ContractWizard } from "@/components/billing/contracts/ContractWizard";
import { TerminationIntakeDialog } from "@/components/billing/contracts/TerminationIntakeDialog";
import { ContractsTable } from "@/components/table/ContractsTable";
import { normalizeContractStatus } from "@/components/billing/contracts/contractStatus";
import type { ContractEffectiveStatus, TransformedContract } from "@/types/billing";
import { toast } from "sonner";
import type { SortingState, ColumnFiltersState } from "@tanstack/react-table";

// Map frontend column IDs to backend sort_by values
const SORT_COLUMN_MAP: Record<string, string> = {
  contractNumber: 'contract_number',
  childName: 'child_name',
  startDate: 'start_date',
  endDate: 'end_date',
  status: 'status',
  createdAt: 'created_at',
};

const STATUS_PRIORITY: readonly ContractEffectiveStatus[] = [
  "IN_EFFECT",
  "UPCOMING",
  "EXPIRED",
  "CANCELLED",
  "TERMINATED",
];

const getStatusPriority = (status?: string) => {
  const normalized = normalizeContractStatus(status);
  const index = STATUS_PRIORITY.indexOf(normalized as ContractEffectiveStatus);
  return index === -1 ? STATUS_PRIORITY.length : index;
};

const getVersionNumber = (contract: TransformedContract) => {
  const record = contract as unknown as Record<string, unknown>;
  const version =
    (record.versionNumber as number | undefined) ??
    (record.version_number as number | undefined);
  return typeof version === "number" ? version : 0;
};

const getDateValue = (contract: TransformedContract, keys: string[]) => {
  const record = contract as unknown as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (value instanceof Date) {
      return value.getTime();
    }
    if (typeof value === "string") {
      const time = Date.parse(value);
      if (!Number.isNaN(time)) {
        return time;
      }
    }
  }
  return 0;
};

export const selectPrimaryContracts = (contracts: TransformedContract[]): TransformedContract[] => {
  if (!contracts?.length) {
    return [];
  }

  const groups = new Map<string, TransformedContract>();

  contracts.forEach((contract, index) => {
    const record = contract as unknown as Record<string, unknown>;
    const groupId =
      (record.contractGroupId as string | undefined) ??
      (record.contract_group_id as string | undefined) ??
      contract.id ??
      `contract-${index}`;

    const current = groups.get(groupId);
    if (!current) {
      groups.set(groupId, contract);
      return;
    }

    const currentPriority = getStatusPriority(current.status);
    const candidatePriority = getStatusPriority(contract.status);

    if (candidatePriority < currentPriority) {
      groups.set(groupId, contract);
      return;
    }

    if (candidatePriority > currentPriority) {
      return;
    }

    const currentVersion = getVersionNumber(current);
    const candidateVersion = getVersionNumber(contract);

    if (candidateVersion > currentVersion) {
      groups.set(groupId, contract);
      return;
    }

    if (candidateVersion < currentVersion) {
      return;
    }

    const candidateStart = getDateValue(contract, ["startDate", "start_date"]);
    const currentStart = getDateValue(current, ["startDate", "start_date"]);

    if (candidateStart > currentStart) {
      groups.set(groupId, contract);
      return;
    }

    if (candidateStart < currentStart) {
      return;
    }

    const candidateCreated = getDateValue(contract, ["createdAt", "created_at"]);
    const currentCreated = getDateValue(current, ["createdAt", "created_at"]);

    if (candidateCreated > currentCreated) {
      groups.set(groupId, contract);
    }
  });

  return Array.from(groups.values());
};

/** Server-side filter params derived from the table's column-filter state. */
export interface MappedServerFilters {
  labelIds?: string;
  subsidised?: boolean;
  subsidyDecisionPending?: boolean;
  hasStaffNotes?: boolean;
  startDateFrom?: string;
  startDateTo?: string;
  endDateFrom?: string;
  endDateTo?: string;
  createdFrom?: string;
  createdTo?: string;
}

/** Format a Date as a local YYYY-MM-DD string (avoids the UTC day-shift of toISOString). */
const toLocalYmd = (d?: Date): string | undefined =>
  d instanceof Date
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
    : undefined;

/**
 * Translate the table's ColumnFiltersState into server-side query params.
 *
 * The contracts table filters server-side, so every filter (labels, the yes/no
 * filters, date ranges) must be mapped here to actually take effect. Exported for
 * testing so a regression that drops a filter is caught by the suite.
 */
export const mapColumnFiltersToServerFilters = (
  filters: ColumnFiltersState
): MappedServerFilters => {
  const valueOf = (id: string) => filters.find((f) => f.id === id)?.value;

  // Labels: array of label-definition IDs.
  const labelsValue = valueOf("labels") as string[] | undefined;
  const labelIds =
    labelsValue && labelsValue.length > 0 ? labelsValue.join(",") : undefined;

  // Yes/no filters: a single selected value maps to the server filter;
  // selecting multiple (or none) means "no filter".
  //   "true"  → <param>=true
  //   "false" → <param>=false
  const booleanOf = (id: string): boolean | undefined => {
    const value = valueOf(id) as string[] | undefined;
    if (!value || value.length !== 1) return undefined;
    if (value[0] === "true") return true;
    if (value[0] === "false") return false;
    return undefined;
  };

  // Date ranges: { from?: Date; to?: Date }.
  const startRange = valueOf("startDate") as { from?: Date; to?: Date } | undefined;
  const endRange = valueOf("endDate") as { from?: Date; to?: Date } | undefined;
  const createdRange = valueOf("createdAt") as { from?: Date; to?: Date } | undefined;

  return {
    labelIds,
    subsidised: booleanOf("subsidised"),
    // Group-level (#1748): any version of the contract group matches.
    subsidyDecisionPending: booleanOf("groupSubsidyDecisionPending"),
    hasStaffNotes: booleanOf("groupHasStaffNotes"),
    startDateFrom: toLocalYmd(startRange?.from),
    startDateTo: toLocalYmd(startRange?.to),
    endDateFrom: toLocalYmd(endRange?.from),
    endDateTo: toLocalYmd(endRange?.to),
    createdFrom: toLocalYmd(createdRange?.from),
    createdTo: toLocalYmd(createdRange?.to),
  };
};

const SCOPES = ["current", "all"] as const;
type ContractScope = (typeof SCOPES)[number];

/** Column filters kept in the URL, so Back from a contract restores the filtered list. */
const FILTER_KEYS = [
  "labels",
  "subsidised",
  "groupSubsidyDecisionPending",
  "groupHasStaffNotes",
  "startDate",
  "endDate",
  "createdAt",
] as const;
type ContractFilterKey = (typeof FILTER_KEYS)[number];
const DATE_FILTER_KEYS: readonly ContractFilterKey[] = ["startDate", "endDate", "createdAt"];

const DEFAULT_SORT: SortingState = [{ id: "createdAt", desc: true }];

/** `2026-01-01..2026-03-31` (either side may be empty) <-> the table's `{ from, to }` range. */
const parseLocalYmd = (value: string): Date | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : undefined;
};

export const columnFiltersFromUrl = (
  filters: Partial<Record<ContractFilterKey, string[]>>
): ColumnFiltersState =>
  FILTER_KEYS.flatMap((key): ColumnFiltersState => {
    const values = filters[key];
    if (!values || values.length === 0) return [];
    if (DATE_FILTER_KEYS.includes(key)) {
      const [from = "", to = ""] = values[0].split("..");
      const range = { from: parseLocalYmd(from), to: parseLocalYmd(to) };
      return range.from || range.to ? [{ id: key, value: range }] : [];
    }
    return [{ id: key, value: values }];
  });

export const urlFiltersFromColumnFilters = (
  filters: ColumnFiltersState
): Partial<Record<ContractFilterKey, string[]>> => {
  const result: Partial<Record<ContractFilterKey, string[]>> = {};
  for (const filter of filters) {
    const key = filter.id as ContractFilterKey;
    if (!FILTER_KEYS.includes(key)) continue;
    if (DATE_FILTER_KEYS.includes(key)) {
      const range = filter.value as { from?: Date; to?: Date } | undefined;
      const from = toLocalYmd(range?.from) ?? "";
      const to = toLocalYmd(range?.to) ?? "";
      if (from || to) result[key] = [`${from}..${to}`];
      continue;
    }
    const values = Array.isArray(filter.value) ? (filter.value as unknown[]).map(String) : [];
    if (values.length > 0) result[key] = values;
  }
  return result;
};

export default function ContractsPage() {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [wizardOpen, setWizardOpen] = useState(false);
  // Child preselected in the wizard by `?action=new-contract&child=<id>`.
  const [wizardChildId, setWizardChildId] = useState<string | undefined>();
  const queryClient = useQueryClient();
  const canOpen = useCanOpen();
  const [contractToEnd, setContractToEnd] = useState<TransformedContract | null>(null);
  const canEndContracts = useModuleGate(CONTRACT_TERMINATION_MODULES);

  // Search, page, sort, scope (current/all) and the column filters live in the
  // URL, so opening a contract and pressing Back restores the list as it was.
  const list = useListUrlState<ContractScope, ContractFilterKey>({
    scopes: SCOPES,
    defaultScope: "current",
    filterKeys: FILTER_KEYS,
    defaultSort: DEFAULT_SORT,
  });
  const scope = list.scope ?? "current";

  // The table seeds its filter chips from the URL once; `key={list.syncKey}`
  // re-mounts it when the URL changes from outside (sidebar, palette, Back).
  const initialColumnFilters = columnFiltersFromUrl(list.filters);
  const columnFilters = useMemo(() => columnFiltersFromUrl(list.filters), [list.filters]);
  const serverFilters = useMemo(() => mapColumnFiltersToServerFilters(columnFilters), [columnFilters]);

  const serverSorting = useMemo(() => {
    const first = list.sort[0];
    const sortBy = first ? SORT_COLUMN_MAP[first.id] : undefined;
    return sortBy
      ? { sortBy, sortOrder: (first.desc ? "desc" : "asc") as "asc" | "desc" }
      : { sortBy: "created_at", sortOrder: "desc" as const };
  }, [list.sort]);

  const queryOptions: ContractsPaginatedOptions = useMemo(() => ({
    status: scope === "current" ? "in_effect,upcoming" : undefined,
    ...serverFilters,
    search: list.search || undefined,
    sortBy: serverSorting.sortBy,
    sortOrder: serverSorting.sortOrder,
  }), [scope, serverFilters, list.search, serverSorting]);

  // `?action=new-contract` (sidebar "+", dashboard quick action, the child
  // page with `&child=<id>`) opens the wizard once; the params are dropped so a
  // reload does not reopen it.
  useEffect(() => {
    if (searchParams.get('action') === 'new-contract') {
      setWizardChildId(searchParams.get('child') || undefined);
      setWizardOpen(true);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('action');
          next.delete('child');
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  // Enrolment does not end at the contract: a child without a group place gets
  // the next step offered in a toast. The create hook already reported success.
  const handleContractCreated = useCallback(
    async (created: { childId: string; childName?: string }) => {
      if (!created.childId || !canOpen('/capacity')) return;
      let hasGroupPlace = false;
      try {
        // Same key as useAllocations(childId), so the child page reuses the answer.
        const filters = {
          childId: created.childId,
          groupId: undefined,
          contractId: undefined,
          limit: 50,
          offset: 0,
          fromDate: undefined,
          toDate: undefined,
        };
        const response = await queryClient.fetchQuery({
          queryKey: allocationKeys.list(filters),
          queryFn: () => AllocationsService.getApiV1Allocations(50, 0, created.childId),
        });
        const allocations = (response as { allocations?: Array<{ id?: string; from_date?: string; to_date?: string }> })?.allocations;
        hasGroupPlace = !!findFirstAllocation(allocations, toOrgDateOnly(new Date().toISOString()));
      } catch {
        // Unknown: offer the step; placing a child twice is caught on the capacity page.
      }
      if (hasGroupPlace) return;
      const childLabel = created.childName?.trim();
      toast.info(
        childLabel
          ? i18n._({ id: "contracts.next.noGroupPlace", message: "{child} has no group place yet.", values: { child: childLabel } })
          : i18n._({ id: "contracts.next.noGroupPlaceUnnamed", message: "The child has no group place yet." }),
        {
          duration: 10000,
          action: {
            label: childLabel
              ? i18n._({ id: "contracts.next.placeInGroup", message: "Place {child} in a group", values: { child: childLabel } })
              : i18n._({ id: "contracts.next.placeInGroupUnnamed", message: "Place in a group" }),
            onClick: () => navigate(`/capacity?child=${encodeURIComponent(created.childId)}`),
          },
        },
      );
    },
    [canOpen, queryClient, i18n, navigate],
  );

  const {
    data: contracts,
    total,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useContractsPage(queryOptions, list.pagination);

  const exportAll = useCallback(() => fetchAllContracts(queryOptions), [queryOptions]);

  // The table reports its filters and sort on mount too. Writing an unchanged
  // value would still reset the page, so Back to page 3 would land on page 1.
  const { filters: urlFilters, setFilters, sort: urlSort, setSort } = list;
  const handleFilterChange = useCallback(
    (filters: ColumnFiltersState) => {
      const next = urlFiltersFromColumnFilters(filters);
      if (JSON.stringify(next) !== JSON.stringify(urlFiltersFromColumnFilters(columnFiltersFromUrl(urlFilters)))) {
        setFilters(next);
      }
    },
    [urlFilters, setFilters]
  );

  const handleSortingChange = useCallback(
    (sorting: SortingState) => {
      const next = sorting.filter((s) => SORT_COLUMN_MAP[s.id]);
      if (JSON.stringify(next) !== JSON.stringify(urlSort)) setSort(next);
    },
    [urlSort, setSort]
  );

  // The error shows inside the table body: a stale or shared URL filter the
  // API rejects stays visible in the toolbar and can be removed.
  const loadError =
    error && !isLoading ? (
      <QueryErrorState
        error={error}
        onRetry={() => void refetch()}
        action={
          list.isFiltered ? (
            <Button type="button" variant="outline" size="sm" onClick={list.reset}>
              {i18n._({ id: "table.filter.clearAll", message: "Clear all filters" })}
            </Button>
          ) : undefined
        }
      />
    ) : undefined;

  const openContract = (contract: TransformedContract) => {
    if (contract.id) {
      navigate(`/contracts/${contract.id}/edit`);
    } else {
      toast.error(i18n._("contracts.view.missingId", {
        defaultMessage: "Contract ID is missing"
      }));
    }
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex justify-end">
          <Button onClick={() => setWizardOpen(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            <Trans id="common.add">Add</Trans>
          </Button>
        </div>

        <ContractsTable
          key={list.syncKey}
          data={loadError ? [] : contracts}
          errorState={loadError}
          onView={openContract}
          onEdit={openContract}
          onEndContract={canEndContracts ? setContractToEnd : undefined}
          enableSelection={true}
          serverSidePagination={true}
          totalRows={loadError ? 0 : total}
          pagination={list.pagination}
          onPaginationChange={list.setPagination}
          isLoading={isLoading}
          isFetching={isFetching}
          onFilterChange={handleFilterChange}
          onSortingChange={handleSortingChange}
          onSearchChange={list.setSearch}
          searchTerm={list.search}
          sorting={list.sort}
          initialColumnFilters={initialColumnFilters}
          exportAll={exportAll}
          onAdd={canOpen("/contracts?action=new-contract") ? () => setWizardOpen(true) : undefined}
          onShowAll={scope === "current" ? () => list.setScope("all") : undefined}
          // The scope is not a filter here: "All" with nothing found is the first-use state.
          hasExternalFilters={list.search !== "" || Object.keys(list.filters).length > 0}
          onClearFilters={list.reset}
          toolbarRight={
            <SegmentedToggle<ContractScope>
              className="ml-auto"
              value={scope}
              onValueChange={list.setScope}
              aria-label={i18n._({ id: "contracts.scope.label", message: "Which contracts to show" })}
              options={[
                { value: "current", label: i18n._({ id: "children.filter.current", message: "Current" }) },
                { value: "all", label: i18n._({ id: "children.filter.all", message: "All" }) },
              ]}
            />
          }
        />
      </div>

      <Dialog
        open={wizardOpen}
        onOpenChange={(open) => {
          setWizardOpen(open);
          if (!open) setWizardChildId(undefined);
        }}
      >
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0" hideCloseButton>
          <DialogTitle className="sr-only">
            {i18n._("contracts.wizard.title", { defaultMessage: "Create New Contract" })}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {i18n._("contracts.wizard.description", { defaultMessage: "Follow the steps to create a new contract for a child" })}
          </DialogDescription>
          <ContractWizard
            initialChildId={wizardChildId}
            onClose={() => {
              setWizardOpen(false);
              setWizardChildId(undefined);
            }}
            onSuccess={(_contractId, created) => {
              setWizardOpen(false);
              void handleContractCreated(created);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* The guided termination: end date and reason here, then the review page
          walks through invoices, exit fees and allocations before anything changes. */}
      <TerminationIntakeDialog
        open={!!contractToEnd}
        onOpenChange={(open) => {
          if (!open) setContractToEnd(null);
        }}
        contractId={contractToEnd?.id}
      />
    </>
  );
}
